import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';

// Lightweight SM-2 variant. Intervals grow on success, reset to 1 day on failure.
const INTERVALS = [1, 3, 7, 16, 35, 90];
const MAX_MISTAKE_ITEMS = 25;

const itemId = (userId, subjectId, topicKey, type) => `${userId}_${subjectId}_${type}_${topicKey}`.replace(/[\s/]/g, '_');

// Older features (Cailyn Live, timed mocks) wrote queue docs as
// { topic, note, createdAt, source } with no scheduling fields. Normalise them
// so they surface as due items instead of silently never appearing.
const normalise = (d) => {
  if (d.dueAt) return d;
  return {
    ...d,
    type: d.type || 'topic',
    topicKey: d.topicKey || d.topic || '',
    label: d.label || d.topic || '',
    stage: d.stage || 0,
    intervalDays: d.intervalDays || 1,
    dueAt: d.createdAt || new Date().toISOString(),
    subjectName: d.subjectName || '',
  };
};

export default function useReviewQueue(userId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchQueue = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [queueSnap, mistakeSnap] = await Promise.all([
        getDocs(query(collection(db, 'reviewQueue'), where('userId', '==', userId))),
        getDocs(query(collection(db, 'mistakeLog'), where('userId', '==', userId), where('resolved', '==', false))),
      ]);
      const queueItems = queueSnap.docs.map(d => normalise({ id: d.id, ...d.data() }));
      // Unresolved mistakes enter the queue directly; rating one "confident"
      // resolves it in the mistake log (see resolveMistake).
      const mistakeItems = mistakeSnap.docs
        .map(d => ({ docId: d.id, ...d.data() }))
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, MAX_MISTAKE_ITEMS)
        .map(m => ({
          id: `mistake_${m.docId}`,
          mistakeId: m.docId,
          userId,
          type: 'mistake',
          topicKey: m.subtopic || m.question?.slice(0, 40) || 'mistake',
          label: m.subtopic || m.question?.slice(0, 60) || 'Logged mistake',
          subjectId: '',
          subjectName: m.subject || '',
          stage: 0,
          intervalDays: 1,
          dueAt: m.timestamp || new Date().toISOString(),
          source: m.source || 'mistakes',
          question: m.question || '',
          options: m.options || null,
          correct: m.correctAnswer || '',
          explanation: m.explanation || '',
        }));
      setItems([...queueItems, ...mistakeItems]);
    } catch (err) { console.error('review queue fetch failed', err); }
    setLoading(false);
  }, [userId]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  // result: 'confident' | 'shaky' | 'wrong' — drives whether interval grows or resets.
  const schedule = useCallback(async (subjectId, topicKey, type, result, meta = {}) => {
    if (!userId) return;
    const id = itemId(userId, subjectId, topicKey, type);
    const ref = doc(db, 'reviewQueue', id);
    try {
      const snap = await getDoc(ref);
      const prev = snap.exists() ? snap.data() : null;
      const succeeded = result === 'confident';
      let stage = prev?.stage || 0;
      stage = succeeded ? Math.min(stage + 1, INTERVALS.length - 1) : 0;
      const intervalDays = INTERVALS[stage];
      const dueAt = new Date(Date.now() + intervalDays * 86400000).toISOString();
      const data = {
        userId, subjectId, topicKey, type, stage, intervalDays, dueAt,
        lastResult: result, lastReviewedAt: new Date().toISOString(),
        subjectName: meta.subjectName || prev?.subjectName || '',
        label: meta.label || prev?.label || topicKey.replace(/_/g, ' '),
        ...(meta.source || prev?.source ? { source: meta.source || prev?.source } : {}),
      };
      await setDoc(ref, data);
      setItems(prevItems => {
        const others = prevItems.filter(i => i.id !== id);
        return [...others, { id, ...data }];
      });
    } catch (err) { console.error('review schedule failed', err); }
  }, [userId]);

  // Marks a mistake-log item resolved and removes it from the queue.
  const resolveMistake = useCallback(async (mistakeId) => {
    try {
      await updateDoc(doc(db, 'mistakeLog', mistakeId), { resolved: true, resolvedAt: new Date().toISOString() });
      setItems(prev => prev.filter(i => i.mistakeId !== mistakeId));
      return true;
    } catch (err) {
      console.error('mistake resolve failed', err);
      return false;
    }
  }, []);

  const dueItems = items.filter(i => new Date(i.dueAt) <= new Date());
  const getDueCount = () => dueItems.length;
  const getDueItems = (limit = 50) => dueItems.sort((a, b) => new Date(a.dueAt) - new Date(b.dueAt)).slice(0, limit);

  return { items, loading, schedule, resolveMistake, getDueCount, getDueItems, refresh: fetchQueue };
}
